import React from "react";
import { Check, X, FileSearch } from "lucide-react";
import api from "../api";

export async function checkAtsResumeFile(file, extra = {}) {
  if (!file) throw new Error("Upload a Resume / CV file to check the ATS score.");
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read that resume file."));
    reader.readAsDataURL(file);
  });
  return api.checkAtsResume({
    filename: file.name,
    data: dataUrl,
    title: extra.title || "",
    skills: extra.skills || extra.strengths || ""
  });
}

export default function AtsResumeChecker({ result, busy, error, theme }) {
  const score = Number(result?.ats_score ?? result?.score ?? 0);
  const band = result?.band || "";
  const color = score >= 80 ? "#16A34A" : score >= 65 ? "#0D6EFD" : score >= 50 ? "#CA8A04" : "#DC2626";
  const line = theme?.line || "#E2E8F0";
  const text = theme?.text || "inherit";
  const mute = theme?.textMute || "#64748B";
  const surface = theme?.surfaceRaised || theme?.surface || "transparent";

  if (!busy && !error && !result) return null;

  return (
    <div className="mt-3 p-3 rounded-3" style={{ background: surface, border: `1px solid ${line}`, color: text }}>
      <div className="d-flex align-items-center gap-2 small fw-bold text-uppercase mb-2" style={{ color: mute, letterSpacing: 0.4 }}>
        <FileSearch size={14} /> ATS Resume Checker
      </div>
      {busy && <div className="small" style={{ color: mute }}>Checking ATS score…</div>}
      {error && <div className="small fw-semibold" style={{ color: "#DC2626" }}>{error}</div>}
      {!busy && result && (
        <>
          <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                border: `4px solid ${color}`,
                color
              }}
            >
              <div className="fw-bold" style={{ fontSize: 22, lineHeight: 1 }}>{score}</div>
              <div style={{ fontSize: 9, fontWeight: 700 }}>ATS</div>
            </div>
            <div>
              <div className="fw-bold" style={{ color }}>ATS Score: {score}/100 · {band}</div>
              <div className="small mt-1" style={{ color: mute }}>{result.summary}</div>
            </div>
          </div>
          {Array.isArray(result.checks) && result.checks.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-2">
              {result.checks.map((c, i) => (
                <span
                  key={i}
                  className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1"
                  style={{
                    fontSize: 11,
                    background: c.ok ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.08)",
                    color: c.ok ? "#16A34A" : "#DC2626",
                    border: `1px solid ${c.ok ? "rgba(22,163,74,0.35)" : "rgba(220,38,38,0.25)"}`
                  }}
                >
                  {c.ok ? <Check size={11} /> : <X size={11} />} {c.label}
                </span>
              ))}
            </div>
          )}
          {Array.isArray(result.tips) && result.tips.length > 0 && (
            <ul className="small mb-0 ps-3" style={{ color: mute }}>
              {result.tips.map((tip, i) => <li key={i}>{tip}</li>)}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
